// Mobile theme derived from the web app's global-styles.js themeBuilder palette.
// Variable names mirror the web theme tokens so both codebases stay in sync.

export const colors = {
  // ─── AI Orb (brand identity — same in both modes) ─────────────────────────
  orbRing: '#C5FFFD',   // aqua/mint ring colour — matches web default

  // ─── Dark theme ───────────────────────────────────────────────────────────
  // Matches: backgroundColor, coreBackground, inputBackground, contentBackground
  backgroundDark: '#2B2F41',   // rgba(43,47,65,1)
  coreBackgroundDark: '#212434', // rgba(33,36,52,1) — slightly deeper
  surfaceDark: '#242838',       // rgba(36,40,56,1) — input / row well
  cardDark: '#34394D',          // rgba(52,57,77,1) — elevated card surface
  sidebarSurfaceDark: 'rgba(31,35,51,0.74)',
  sidebarBorderDark: 'rgba(255,255,255,0.16)',
  sidebarActiveDark: 'rgba(99,184,255,0.16)',
  sidebarActiveSubDark: 'rgba(99,184,255,0.10)',

  textDark: '#FFFFFF',          // rgba(255,255,255,1)
  mutedDark: '#A1A3AC',         // rgba(161,163,172,1)
  borderDark: 'rgba(255,255,255,0.10)',  // faintBorder dark

  // Primary action + text on primary
  primaryDark: '#63B8FF',       // rgba(99,184,255,1) — buttonBackground dark
  primaryTextDark: '#212434',   // rgba(33,36,52,1)  — buttonTextColor dark

  // ─── Light theme ──────────────────────────────────────────────────────────
  backgroundLight: '#FFFFFF',
  coreBackgroundLight: '#F9F9F9', // rgba(249,249,249,1)
  surfaceLight: '#F5F6FA',        // faintBackground
  cardLight: '#FFFFFF',
  sidebarSurfaceLight: 'rgba(255,255,255,0.84)',
  sidebarBorderLight: 'rgba(105,107,116,0.18)',
  sidebarActiveLight: 'rgba(238,108,76,0.15)',
  sidebarActiveSubLight: 'rgba(238,108,76,0.10)',

  textLight: '#696B74',           // rgba(105,107,116,1) — headerTextColor light
  textBodyLight: '#343740',       // rgba(52,55,64,1)    — body text
  mutedLight: '#696B74',          // rgba(105,107,116,1)
  borderLight: 'rgba(105,107,116,0.20)', // faintBorder light

  primaryLight: '#EE6C4C',        // rgba(238,108,76,1) — buttonBackground light
  primaryTextLight: '#FFFFFF',
  // Darker primary for small filled controls: white on #C24A2C is 4.9:1 (AA); on primaryLight only 3:1.
  primaryStrongLight: '#C24A2C',

  // ─── Shared status colours ─────────────────────────────────────────────────
  //
  // Opaque colours are written as 6-digit hex, never as `rgba(r,g,b,1)`.
  // Components tint these by appending a hex alpha byte — `${color}14` for an
  // 8% wash behind an icon, `${color}1A` for a badge — an idiom used in ~350
  // places. That concatenation only produces a colour when the base is hex:
  // `'rgba(40,167,69,1)' + '14'` is not a valid colour, and instead of the
  // intended 8% wash the row rendered a SOLID block of the same green the
  // icon was drawn in, so the icon vanished into its own background.
  // The alpha-1 rgba forms below were identical in colour to the hex in
  // their own trailing comments; they are now simply written that way.
  error: '#F44337',               // errorRed
  success: '#28A745',
  warning: '#F59E0B',
  teal: '#0096B1',
  lightBlue: '#63B8FF',
  electricBlue: '#00D9FF',
  gold: '#FFC000',
  white: '#FFFFFF',
  black: '#000000',
  overlay: 'rgba(0,0,0,0.5)',
};

// ─── Feature colour palette ──────────────────────────────────────────────────
// Centralised secondary palette for status / category / service-brand colours
// that are NOT part of the primary theme but are reused across multiple
// components. Previously these lived as inline hex literals in 80+ call sites
// (HealthValueShowcase, FastenConnectionsWidget, KBSuggestionsSheet, etc.).
//
// Two intents:
//   1. Single source of truth → if a designer wants to shift the "warning
//      amber" everywhere, edit one line here.
//   2. Future whitelabel hook → eventually getWhitelabelPalette can overlay
//      these too, so partner brands can recolour status badges.
//
// Adoption is OPT-IN: existing inline hex literals continue to work. Migrate
// when you touch a file for unrelated reasons.
export const featureColors = {
  // Status / categorisation hues — reach for these when building badges,
  // section headers, or icon wells that need to differentiate categories.
  red:     '#EF4444',
  amber:   '#F59E0B',
  green:   '#10B981',
  emerald: '#22C55E',
  blue:    '#0EA5E9',
  indigo:  '#6366F1',
  purple:  '#8B5CF6',
  pink:    '#EC4899',
  orange:  '#F97316',
  cyan:    '#0284C7',
  sky:     '#60A5FA',
  slate:   '#6B7280',

  // Service-brand colours used by integration cards / cloud pickers.
  googleBrand:   '#4285F4',
  dropboxBrand:  '#0061FF',
  appleGreen:    '#34C759',
  inboxAmber:    '#F59E0B',
} as const;

export type FeatureColorKey = keyof typeof featureColors;

// ─── Shadow / elevation tokens ───────────────────────────────────────────────
// Centralised shadow definitions for consistent depth across iOS and Android.
// Each level returns the full property set RN needs (iOS shadow* + Android
// elevation), so consumers can spread one object instead of hand-rolling
// platform-specific shadow code (which the audit found 8+ times with drift).
//
// Usage: spread a level into a style object. Don't mutate.
//   const styles = StyleSheet.create({ card: { ...theme.shadow.medium, ... } });
//
// Levels are tuned to match existing hand-rolled shadows in the codebase
// (WalgreensRefillModal, DesktopUpdateBanner, WatchNoteProcessedSheet).
export const shadows = {
  none: {
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  subtle: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 1,
  },
  card: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.10,
    shadowRadius: 8,
    elevation: 3,
  },
  medium: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.14,
    shadowRadius: 12,
    elevation: 6,
  },
  elevated: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.18,
    shadowRadius: 16,
    elevation: 8,
  },
  modal: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.22,
    shadowRadius: 24,
    elevation: 12,
  },
} as const;

export type ShadowLevel = keyof typeof shadows;

export const getTheme = (mode: 'light' | 'dark') => ({
  dark: mode === 'dark',
  colors: {
    // Surfaces
    background:     mode === 'dark' ? colors.backgroundDark     : colors.backgroundLight,
    coreBackground: mode === 'dark' ? colors.coreBackgroundDark : colors.coreBackgroundLight,
    surface:        mode === 'dark' ? colors.surfaceDark        : colors.surfaceLight,
    card:           mode === 'dark' ? colors.cardDark           : colors.cardLight,
    sidebarSurface: mode === 'dark' ? colors.sidebarSurfaceDark : colors.sidebarSurfaceLight,
    /** Recessed well for secondary buttons and chips (Reject / Cancel, tabs). */
    subtleSurface:  mode === 'dark' ? colors.surfaceDark        : colors.surfaceLight,

    // Text
    text:           mode === 'dark' ? colors.textDark           : colors.textBodyLight,
    headerText:     mode === 'dark' ? colors.textDark           : colors.textLight,
    muted:          mode === 'dark' ? colors.mutedDark          : colors.mutedLight,
    /** Same value as `muted`; several components read it under this name. */
    textMuted:      mode === 'dark' ? colors.mutedDark          : colors.mutedLight,

  // Borders
  border:         mode === 'dark' ? colors.borderDark         : colors.borderLight,
  sidebarBorder:  mode === 'dark' ? colors.sidebarBorderDark  : colors.sidebarBorderLight,
  sidebarActive:  mode === 'dark' ? colors.sidebarActiveDark  : colors.sidebarActiveLight,
  sidebarActiveSub: mode === 'dark' ? colors.sidebarActiveSubDark : colors.sidebarActiveSubLight,
  /** Stronger border for form fields so outlines are visible in dark mode */
  formFieldBorder: mode === 'dark' ? 'rgba(255,255,255,0.24)' : colors.borderLight,

    // Primary action
    primary:        mode === 'dark' ? colors.primaryDark        : colors.primaryLight,
    primaryText:    mode === 'dark' ? colors.primaryTextDark    : colors.primaryTextLight,
    primaryStrong:  mode === 'dark' ? colors.primaryDark        : colors.primaryStrongLight,
    /** Low-alpha primary wash (selected chips, highlighted cards). */
    primarySubtle:  `${mode === 'dark' ? colors.primaryDark : colors.primaryLight}15`,

    // Status
    error:          colors.error,
    success:        colors.success,
    warning:        colors.warning,
    teal:           colors.teal,
    lightBlue:      colors.lightBlue,

    // AI Orb & brand accent (same in both modes)
    orbRing:        colors.orbRing,
    electricBlue:   colors.electricBlue,
  },
  typography: { family: 'system', scale: 1, letterSpacing: 0 },
  components: {
    projectChatBubbleRadius: 16, accountChatBubbleRadius: 18, chatBubblePadding: 14, chatBubbleBorderWidth: 1, chatTextSize: 15,
    composerRadius: 12, composerBorderWidth: 1, composerPadding: 16, composerTextSize: 15,
    desktopRadius: 18, desktopMaxWidth: 1400, desktopBorderWidth: 1, desktopTintOpacity: 0.1,
  },
  // Semantic spacing scale. EXISTING — do not change values; consumers
  // depend on the exact pixel sizes here. New interim values are exposed via
  // `spacingPx` below.
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
  },
  // Pixel-precise spacing additions for cases that don't fit the semantic
  // scale. Audit found ~120 hardcoded values clustering around 10/12/14/18/20.
  // Use these instead of magic numbers; reach for the semantic `spacing`
  // scale first.
  spacingPx: {
    px2: 2,
    px6: 6,
    px10: 10,
    px12: 12,
    px14: 14,
    px18: 18,
    px20: 20,
    px28: 28,
    px40: 40,
  },
  // Semantic radius scale. EXISTING — do not change values.
  radius: {
    sm: 6,
    md: 12,
    lg: 16,
    full: 9999,
  },
  // Pixel-precise radius additions. Audit found ~180 hardcoded radii;
  // most cluster around 2 (sheet handles), 8 (small cards), 10 (buttons),
  // 14 (cards), 20 (large cards), 28+ (avatars).
  radiusPx: {
    px2: 2,    // sheet handles, dividers
    px8: 8,    // small cards / inputs
    px10: 10,  // buttons / pill-style chips
    px14: 14,  // mid-sized cards
    px20: 20,  // large cards / large buttons
    px28: 28,  // avatar wells
  },
  font: {
    // Semantic font scale. EXISTING — do not change values.
    size: {
      xs: 11,
      sm: 13,
      md: 15,
      lg: 17,
      xl: 20,
      xxl: 24,
      title: 30,
    },
    // Pixel-precise font additions. Audit found ~250 hardcoded font sizes
    // clustering around 12 (82×), 14 (68×), 16 (53×), 18 (10×) — none in
    // the semantic scale. Use these instead of magic numbers.
    sizePx: {
      px10: 10,
      px12: 12,
      px14: 14,
      px16: 16,
      px18: 18,
    },
    weight: {
      regular: '400' as const,
      medium: '500' as const,
      semibold: '600' as const,
      bold: '700' as const,
    },
  },
  // Icon sizing scale. NEW. Audit found ~150 icon call sites with sizes
  // scattered 10–48px and no convention. Use this scale for new code; the
  // mapping mirrors the most-common existing values so adoption is a
  // 1:1 swap.
  icon: {
    size: {
      xs: 12,    // dense inline icons (64% of historical "small" usage)
      sm: 16,    // standard inline icons
      md: 20,    // section headers, list items
      lg: 24,    // primary actions, large buttons
      xl: 32,    // intro/feature illustrations
      xxl: 48,   // hero / loading states
    },
  },
  // Shadow / elevation tokens (see `shadows` const above for definitions).
  shadow: shadows,
  // Feature/secondary palette (see `featureColors` const above).
  featureColors,
});

export type AppTheme = ReturnType<typeof getTheme>;
